import type { DealRejected, DealReply, FillReport } from '@/api/signalR/messages/execution.ts';
import type { PingMessage } from '@/api/signalR/worker/ping/Pinger.ts';
import type { BlotterOrderEntryFromBackend } from '@/store/blotter/api/types/BlotterOrderEntryFromBackend.ts';
import type { BlotterTradeEntry } from '@/store/blotter/api/types/BlotterTradeEntry.ts';
import type { EspHeartbeat, EspPrice } from '@/store/esp/slice.ts';
import type { SharedTabMessageData } from '@/store/workspace/useSharedWorkspaceSignalrMessages.ts';
import type { HeartbeatMessage, IQuoteAbort, IQuoteReply, IQuoteWithdraw, QuoteType } from '@/utils/rfs/type.ts';

export type SignalRTopics = {
  // ESP
  ReceiveEspPrice: [EspPrice];
  ReceiveEspHeartbeat: [EspHeartbeat];

  // RFS
  // TODO: we can simplify logic in option/accu/flexi because they were handling impossible quote types
  // ReceiveQuoteReply: [QuoteReply];
  // ReceiveQuoteWithdraw: [QuoteReply];
  ReceiveQuoteReply: [IQuoteReply<QuoteType>];
  ReceiveQuoteWithdraw: [IQuoteWithdraw<QuoteType>];
  ReceiveQuoteAbort: [IQuoteAbort];
  ReceiveHeartbeat: [HeartbeatMessage];

  // Blotter
  ReceiveOrderNotification: [BlotterOrderEntryFromBackend[]];
  ReceiveTradeNotification: [BlotterTradeEntry[]];

  // Exec
  ReceiveDealReply: [DealReply];
  ReceiveDealRejected: [DealRejected];
  ReceiveFillReport: [FillReport];

  // Shared tabs
  SharedTabRemoved: [SharedTabMessageData];
  SharedTabUnsubscribed: [SharedTabMessageData];
  SharedTabUpdated: [SharedTabMessageData];

  // Other
  ReceiveForceLogOff: [];

  // Ping
  ReceivePing: [PingMessage];

  // Unused
  ReceiveConnection: [];
  ReceiveMessage: [];
};

export type SignalRTopic = keyof SignalRTopics;
export type SignalRArgs<K extends SignalRTopic> = SignalRTopics[K];
export type SignalRListener<K extends SignalRTopic> = (...args: SignalRArgs<K>) => void;

const signalRTopicsKeys = {
  ReceiveEspPrice: null,
  ReceiveEspHeartbeat: null,
  ReceiveQuoteReply: null,
  ReceiveQuoteWithdraw: null,
  ReceiveQuoteAbort: null,
  ReceiveHeartbeat: null,
  ReceiveOrderNotification: null,
  ReceiveTradeNotification: null,
  ReceiveDealReply: null,
  ReceiveDealRejected: null,
  ReceiveFillReport: null,
  SharedTabRemoved: null,
  SharedTabUnsubscribed: null,
  SharedTabUpdated: null,
  ReceiveForceLogOff: null,
  ReceivePing: null,
  ReceiveConnection: null,
  ReceiveMessage: null,
} as const satisfies Record<SignalRTopic, null>;

export const ALL_SIGNALR_TOPICS = Object.keys(signalRTopicsKeys) as SignalRTopic[];

export type RfsTopic = 'ReceiveQuoteReply' | 'ReceiveQuoteWithdraw' | 'ReceiveQuoteAbort';
